import {
  select,
  call,
  put,
} from 'redux-saga/effects';
import FrontSonda from '@gcloud-npm/utils.monitoring.front-sonda';
import { getErrorInfo } from '@bitComponents/telcovid/ErrorManagementUtils';
import { isNumber } from '@bitComponents/utils/common';
import {
  getProvisionalAgreement as getProvisionalAgreementBit,
} from '../bitSagas/getProvisionalAgreement';
import {
  updateCQ as updateCQBit,
  UPDATE_CQ_RISK_ERROR,
} from '../bitSagas/updateCQ';
import {
  getLegalTerms as getLegalTermsBit,
} from '../bitSagas/getLegalTerms';
import {
  getCQPrices as getCQPricesBit,
} from '../bitSagas/getCQPrices';
import {
  confirmCQ as confirmCQBit,
} from '../bitSagas/confirmCQ';
import * as NextStepSagas from './nextSteps';
import { actions as loaderControlActions } from '../reducers/loaderControl';
import { actions as uiNeedsActions } from '../reducers/uiNeeds';
import { commonActions } from '../reducers/common';
import { actions as taggingActions } from '../reducers/tagging';
import { updateCA } from './ascertain';
import { initCardPayment } from './payment';
import { sendToSE, sendBackToSchoolToSE } from './se';
import { STRING_KEYS } from '../localizedStrings';
import {
  isClientWithRiskError,
  isClientWithRiskDocNeededError,
  getAppliedPromotions,
  isR2RWithoutRequiredDataToSign,
} from './normalizers/cq';
import { normalizeRiskEvaluationInfoParams, normalizeRiskFormRequirements } from './normalizers/scoring';
import { CQ_POTS } from '../catalogConstants';
import { normalizeShoppingCartPrices } from './normalizers/prices';
import { isFundingPayment } from '../utils/payment';
import { megaMinderStatus } from '../minder/MegaMinder/megaMinderStatus';
import { FLOW_TYPES } from '../flowDefinition/flowTypes';
import { getPurchasePriceChange } from './customizer';
import { detectNetflixPOatDisagregate, PRODUCT_TYPES } from '../utils/catalog';
import { evaluateCartConsistency, increaseCartWithDisagregateInfo, removeCartProductsPrice, removeNetflixFromCartInfo } from '../utils/normalizers';
import { PARENT_PRODUCT_TYPES } from './products';
import { MASKS } from '../constants/uxConstants';
import { USER_TYPE } from '../bitConstants/constants';
import { forceOBD } from '../bitSagas/forceOBD';
import { setPurchaseInProgressInSecureUserStorage } from './utils/secureUtils';
import { sendRiskToGenesys } from './genesys';


const apisNeedsStore = state => state.apiNeeds;
const localizedStringsStore = state => state.localizedStrings;
const flowStore = state => state.flow;
const offerStore = state => state.offer;
const getErrorManagement = state => state.errorManagement;
const contextStore = state => state.context.data;
const manualStore = state => state.manual;

export function* getLegalTerms(input) {
  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    successUpdate: commonActions.setLegalTermsInfo,
    errorUpdate: showError,
  };
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);
  const { userType } = yield select(contextStore);

  const { cqId, afterSaleProduct, poptSelected } = apiNeeds;
  const { flowJson } = flowInfo;
  const { flowName } = flowJson;

  const getLegalTermsParams = {
    cqId,
    reduxUpdate,
    normalizeHttpErrors,
  };
  try {
    if (flowName !== FLOW_TYPES.DEVICES) {
      const response = yield call(getLegalTermsBit, getLegalTermsParams);
      if ((flowName === FLOW_TYPES.R2R || flowName === FLOW_TYPES.R2R_2) && isR2RWithoutRequiredDataToSign(response)) {
        const { handledErrors } = yield select(getErrorManagement);
        yield put(showError(handledErrors.BUSINESS_ERRORS.WITHOUT_REQUIRED_DATA_TO_SIGN));
        const frontSonda = new FrontSonda({
          processName: 'getLegalTerms',
          filePath: 'sagas/cq',
        });
        frontSonda.severalMessage({
          info: `No list of phones received to firm OTP. CQ: ${cqId}. Params: ${window.location.search}`,
        });
      }
    }

    const { localizedStrings } = yield select(localizedStringsStore);
    yield put(
      loaderControlActions.setLoaderState({
        loading: true,
        loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.getLegalTerms],
      }),
    );
    if (!afterSaleProduct) {
      yield call(NextStepSagas.getNextSteps, input);
    }
    yield call(getPurchasePriceChange, input);
    if(poptSelected === CQ_POTS.PAYMENT_POT.CARD && userType === USER_TYPE.OPEN && !flowJson.isRegisteredShopper){
      const initCardPaymentParams = {
        normalizeHttpErrors,
        showError,
      };
      yield call(initCardPayment, initCardPaymentParams);
    }
    if(flowJson.automaticConfirmCQ){
      const confirmCQParams = {
        normalizeHttpErrors,
        showError,
      };
      yield call(confirmCQ, confirmCQParams);
    }
  } catch (error) {
    if (isClientWithRiskError(error)) {
      let riskError = null;
      if (error?.response?.status === 'KO'
        && error?.response?.error?.codigoErrorNegocio
      ) {
        riskError = error.response.error.codigoErrorNegocio;
      }
      yield put(uiNeedsActions.setClientWithRisk(riskError));
    }
    return null;
  }
  return null;
}

export function* getCQPrices(input) {
  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    errorUpdate: showError,
  };
  const apiNeeds = yield select(apisNeedsStore);
  const offer = yield select(offerStore);
  const { flowJson } = yield select(flowStore);
  
  const {
    cqId,
    getPricesNeeds,
    disagregateProducts,
    priceChange,
    myProductsInfo,
    isMaintainingPromotion,
    afterSaleProduct,
    afterSaleParentProduct,
    poitSelected,
    poptSelected,
  } = apiNeeds;
  const { vval, productOffering, poit } = offer;

  let clientPackProductOfferingId = null;

  if (myProductsInfo?.convergentMainPoId) {
    clientPackProductOfferingId = myProductsInfo.convergentMainPoId;
  }


  const getCQPricesParams = {
    cqId,
    chosenActions: getPricesNeeds.chosenActions,
    reduxUpdate,
    normalizeHttpErrors,
  };
  let removeNetflixFromCQPrices = false;

  const { localizedStrings } = yield select(localizedStringsStore);
  const { extendSelectElementsNeeds: manualExtendSelectElementsNeeds } = yield select(manualStore);
  
  let manualPhoneNumberToPort = null;
  if(manualExtendSelectElementsNeeds?.mainOptionsObj?.phoneNumber){
    manualPhoneNumberToPort = manualExtendSelectElementsNeeds.mainOptionsObj.phoneNumber;
  }
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.getCQPrices],
    }),
  );
  let response = null;
  try {
    response = yield call(getCQPricesBit, getCQPricesParams);
    // Normalizar la respuesta de getCQPrices
    if (!response || !response.components) {
      return null;
    }
    let responseNormalized = yield call(normalizeShoppingCartPrices, response.components, manualPhoneNumberToPort);

    if (responseNormalized) {
      if(responseNormalized.taggingChoices){
        yield put(taggingActions.setPricesFinalChoices(responseNormalized.taggingChoices));
      }
      if (responseNormalized?.recurringCharges?.products?.length > 0) {
        const downgrades = responseNormalized.recurringCharges.products.filter(product => product.isDowngraded === true);

        if (downgrades) {
          const offerInfo = yield select(offerStore);

          const { productOfferingsToDowngradeForPrices, netflixToDowngrade } = offerInfo;

          const downgradesByIncompatibility = [];

          downgrades.forEach(downgrade => {

            if (downgrade?.productOffering?.id) {
              if (downgrade.productType === PRODUCT_TYPES.NETFLIX) {
                if (flowJson.isAddonFlow && !netflixToDowngrade) {
                  downgradesByIncompatibility.push(downgrade);
                }
              } else if (!productOfferingsToDowngradeForPrices || productOfferingsToDowngradeForPrices?.indexOf(downgrade.productOffering.id) === -1) {
                downgradesByIncompatibility.push(downgrade);
              }
            }
          })

          if (downgradesByIncompatibility.length > 0) {
            yield put(uiNeedsActions.setDowngradesByIncompatibility(downgradesByIncompatibility));
          }
        }

        const r2rChangedArray = responseNormalized.recurringCharges.products.filter(product => product.productType === PRODUCT_TYPES.INCLUDED_R2R);

        if (r2rChangedArray) {
          const r2rChanged = [];

          r2rChangedArray.forEach(r2r => {
            const { finalPrice, productOffering } = r2r;

            const { text } = productOffering;

            if (text && isNumber(finalPrice)) {
              r2rChanged.push({
                name: text,
                price: finalPrice,
              })
            }
          });

          if (r2rChanged.length > 0) {
            yield put(uiNeedsActions.setR2RPriceChange(r2rChanged));
          }
        }
      }

      if (isNumber(vval) && responseNormalized
        && responseNormalized.recurringCharges
        && responseNormalized.recurringCharges.groupPrice !== vval
        && flowJson.flowName !== FLOW_TYPES.PACK_R2R
      ) {
        yield put(uiNeedsActions.setPricesDiscrepancy(true));
      }

      if (disagregateProducts) {
        responseNormalized = increaseCartWithDisagregateInfo(disagregateProducts, productOffering, responseNormalized);
        removeNetflixFromCQPrices = !detectNetflixPOatDisagregate(disagregateProducts);
        if (removeNetflixFromCQPrices && !isMaintainingPromotion) {
          responseNormalized = removeNetflixFromCartInfo(responseNormalized);
        }
      }
      const { hidePrices, cartText, linkItem } = { ...priceChange };
      if (hidePrices) {
        responseNormalized = removeCartProductsPrice(responseNormalized);
      } else {
        evaluateCartConsistency(responseNormalized, clientPackProductOfferingId, cqId);
      }
      responseNormalized.changePriceText = cartText;
      responseNormalized.changePriceConditions = linkItem;

      if (afterSaleProduct?.name || flowJson?.flowName === FLOW_TYPES.EQUIPMENT) {
        if (responseNormalized?.oneTimeCharges?.products
          && Array.isArray(responseNormalized.oneTimeCharges.products)
          && responseNormalized.oneTimeCharges.products.length === 1
          && responseNormalized.oneTimeCharges.products[0]?.productOffering
        ) {
          
            if (responseNormalized.oneTimeCharges.products[0]?.resourceSpecification?.name) {
              let resourceSpecificationName = responseNormalized.oneTimeCharges.products[0].resourceSpecification.name;
              responseNormalized.oneTimeCharges.products[0].productOffering.text = resourceSpecificationName;
              yield put(taggingActions.setAliasSellingProduct(resourceSpecificationName));
            }
            
            if (afterSaleProduct?.name) {
              responseNormalized.oneTimeCharges.products[0].productOffering.text = afterSaleProduct.name;
            }

            
            const poitToShow = poit || poitSelected;

            if (afterSaleParentProduct) {
              if (poitToShow === CQ_POTS.INSTALLATION_POT.AUTO_INSTALLATION_ID) {
                yield put(commonActions.setAddressInfo({ fullAddress: afterSaleParentProduct.label }));
              } else if (poitToShow === CQ_POTS.INSTALLATION_POT.INSTALLATION_ID) {
                yield put(commonActions.setInstallationAddress({ fullAddress: afterSaleParentProduct.label }));
              }
            }

            if (poitToShow) {
              const installationText = localizedStrings[STRING_KEYS.deviceInstallation[poitToShow].additionalInfoText];
              if(installationText){
                responseNormalized.oneTimeCharges.products[0].additionalInfo = installationText;
              }
            }
          }
      }

      if (flowJson.flowName === FLOW_TYPES.R2R_AUTO_RENEWAL) {
        const { r2rToCancelInfo } = apiNeeds;
        
        if (r2rToCancelInfo) {
          const { previousDeviceName, poId, cancellationFee } = r2rToCancelInfo;

          responseNormalized.oneTimeCharges.products = [
            {
              finalPrice: cancellationFee,
              productOffering: {id: poId, text: `Finalización contrato ${previousDeviceName}`},
            },
            ...responseNormalized.oneTimeCharges.products,
          ];

          responseNormalized.oneTimeCharges.groupPrice = cancellationFee;
        }
      }
    }
    yield put(uiNeedsActions.setPricesInfo(responseNormalized));
    // TODO ver con hector -> Siguiente linea, hay que enviar para taguear las promociones que aplican
    // Pero hay que obtenerlas de la salida del prices que ahora mismo se normaliza en redux (setPricesInfo). ->  problema de normalizar dos veces aqui y en redux
    // Alternativa 1: no seria mejor normalizar en sagas y despachar a reducers que se consideren? si no para esto tenemos que normalizar en dos puntos lo mismo.
    // Alternativa 2: que el bitSagas lance varios reducers y cada uno se guarde lo que quiera? mismo problema de normalizar dos veces
    // Alternativa 3: que el reducer que lanza bitSagas en este caso sea de common y cada uno (tagging vs uiNeeds) se quede lo que considere? mismo problema de normalizar dos veces
    const promotions = getAppliedPromotions(response.components);
    yield put(commonActions.setAllPromosId(promotions));

    const appliedNewPromotions = getAppliedPromotions(response.components, true);

    const { promotionsObj } = offer;
    
    const allReceivedPromotions = Object.values(promotionsObj ?? {}).filter(Array.isArray).flat();

    const someReceivedPromoNotApplied = allReceivedPromotions.some(promo => !appliedNewPromotions.includes(promo));

    if(someReceivedPromoNotApplied){
      const frontSonda = new FrontSonda({
        processName: 'getCQPrices',
        filePath: 'sagas/cq',
      });
      const { clientInfo } = yield select(apisNeedsStore);
      const { handledErrors } = yield select(getErrorManagement);
      const { search } = window.location;
      frontSonda.severalMessage({
        info: `ERROR PROMOCIONES APLICADAS: Se reciben las siguientes promociones: ${allReceivedPromotions.join(",")} y sólo se aplican estas: ${appliedNewPromotions.join(",")}. id: ${clientInfo?.userIdPartyHash} url: ${search}`,
      });
      yield put(showError(handledErrors.BUSINESS_ERRORS.PROMOTION_NOT_APPLIED));
      return null;
    }
    
    if (!isFundingPayment(poptSelected)) {
      yield call(getLegalTerms, input);
    }
    return null;
  } catch (error) {
    console.log(error);
    const errorResponse = megaMinderStatus.KO;
    errorResponse.errorInfo = error;

    return errorResponse;
  }
}

export function* updateCQ(input) {
  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    successUpdate: uiNeedsActions.setCANeeds,
    errorUpdate: showError,
  };
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);
  let locationId = null;

  if (apiNeeds && apiNeeds.addressInfo && apiNeeds.addressInfo.id) {
    locationId = apiNeeds.addressInfo.id;
  }

  let contactPhone = null;

  if (apiNeeds
    && apiNeeds.personalContactInfo
    && apiNeeds.personalContactInfo.contactPhone) {
    ({ contactPhone } = apiNeeds.personalContactInfo);
  } else if (apiNeeds
    && apiNeeds.clientInfo
    && apiNeeds.clientInfo.contactPhone
    && apiNeeds.clientInfo.contactPhone.value
  ) {
    contactPhone = apiNeeds.clientInfo.contactPhone.value;
  } else if ( apiNeeds?.register?.inputs?.contactPhone ) {
    contactPhone = apiNeeds.register.inputs.contactPhone;
  }
  contactPhone = contactPhone.replaceAll(MASKS.phone.separator,'');

  const choices = [];

  if (apiNeeds.poitSelected) {
    choices.push({
      id: apiNeeds.poitSelected,
      type: CQ_POTS.INSTALLATION_POT.TYPE,
    });
  }

  if (apiNeeds.poptSelected) {
    choices.push({
      id: apiNeeds.poptSelected,
      type: CQ_POTS.PAYMENT_POT.TYPE,
    });
  }

  if (apiNeeds.poshtSelected) {
    if (flowInfo?.flowJson?.sendInstallationAddressIfInstallerSelected) {
      if (apiNeeds.poitSelected === CQ_POTS.INSTALLATION_POT.INSTALLATION_ID) {
        const { parentProductIdSelected, myProductsInfo } = apiNeeds;
        if (parentProductIdSelected ) {
          const { rawData: myProducts } = myProductsInfo;
          const { parentProductType } = yield select(offerStore);

          const productsToUse = [];

          myProducts.forEach(myProduct => {
            if (myProduct[PARENT_PRODUCT_TYPES[parentProductType]]
              && myProduct.phone
              && myProduct.phone.linesPhone
              && myProduct.phone.linesPhone.length === 1
            ) {
              const { locationId } = myProduct.phone.linesPhone[0];
              const { productId } = myProduct[PARENT_PRODUCT_TYPES[parentProductType]];
      
              if (productId === parentProductIdSelected) {
                productsToUse.push({
                  locationId
                });
              }
            }

            if (productsToUse.length === 1) {
              locationId = productsToUse[0].locationId;
            }
          });
        }
      }
    }

    choices.push({
      id: apiNeeds.poshtSelected,
      type: CQ_POTS.SHIPPING_POT.TYPE,
    });
  }

  const { cqId, riskEvaluationInfo } = apiNeeds;

  let riskEvaluation = null;

  if (riskEvaluationInfo) {
    riskEvaluation = normalizeRiskEvaluationInfoParams(riskEvaluationInfo, contactPhone);
  }

  const updateCQParams = {
    cqId,
    reduxUpdate,
    locationId,
    sms: contactPhone,
    phone: contactPhone,
    normalizeHttpErrors,
    POTsChoices: choices,
    riskEvaluation,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.updateCQ],
    }),
  );
  let response = null;
  try {
    response = yield call(updateCQBit, updateCQParams);

    if (response?.risk?.code === UPDATE_CQ_RISK_ERROR
      && response.risk.evaluationFormValues
    ) {
      if(!flowInfo?.flowJson?.allowScoring){
        const frontSonda = new FrontSonda({
          processName: 'updateCQ',
          filePath: 'sagas/cq',
        });
        frontSonda.severalMessage({
          info: `Scoring not allowed on this flow: ${flowInfo.flowName}`,
        });
      }
      const riskFormRequirements = yield call(normalizeRiskFormRequirements, response.risk.evaluationFormValues);
      yield put(uiNeedsActions.setRiskEvaluationRequirements(riskFormRequirements));

      return megaMinderStatus.LOGICAL_ERROR;
    }
  } catch (error) {
    if(error?.response?.error?.codigoErrorNegocio && flowInfo?.flowJson?.forceOBDWhenErrors?.includes(error.response.error.codigoErrorNegocio)){
      
      let onboardingURL;
      try {
        onboardingURL = yield call(forceOBD, { url: window.location.href });
      } catch (error) {
        console.log(error);
      }
      const { handledErrors } = yield select(getErrorManagement);
      if(onboardingURL){
        yield put(
          showError({
            ...handledErrors.BUSINESS_ERRORS.ONBOARDING_NEEDED,
            errorInfo: {
              ...handledErrors.BUSINESS_ERRORS.ONBOARDING_NEEDED.errorInfo,
              buttons: [
                {
                  ...handledErrors.BUSINESS_ERRORS.ONBOARDING_NEEDED.errorInfo.buttons[0],
                  link: onboardingURL,
                },
              ],
            },
          })
        );
      } else {
        yield put(showError(handledErrors.BUSINESS_ERRORS.ONBOARDING_NEEDED));
      }
    } else {
      if (isClientWithRiskError(error)) {
        let riskError = null;
        if (error?.response?.status === 'KO'
          && error?.response?.error?.codigoErrorNegocio
        ) {
          riskError = error.response.error.codigoErrorNegocio;
        }
        yield put(uiNeedsActions.setClientWithRisk(riskError));
      }
      if (isClientWithRiskDocNeededError(error)) {
        let riskError = null;
        if (error?.response?.status === 'KO'
          && error?.response?.error?.codigoErrorNegocio
        ) {
          riskError = error.response.error.codigoErrorNegocio;
        }
        yield call(sendRiskToGenesys, {...input, payload:{ error: riskError }});
      }
      const errorResponse = megaMinderStatus.KO;
      errorResponse.errorInfo = error;
  
      return errorResponse;
    }
  }

  if (response && response.requiredAccount && !apiNeeds.accountInfo) {
    yield put(loaderControlActions.setLoaderState({
      loading: false,
    }));
  } else {
    return yield call(updateCA, input);
  }
  return null;
}

export function* getProvisionalAgreement(input) {
  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    successUpdate: uiNeedsActions.setProvisionalAgreementData,
    errorUpdate: showError,
  };

  const apiNeeds = yield select(apisNeedsStore);

  const { cqId } = apiNeeds;

  const getProvisionalAgreementParams = {
    cqId,
    reduxUpdate,
    normalizeHttpErrors,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.getProvisionalAgreement],
    }),
  );
  try {
    yield call(getProvisionalAgreementBit, getProvisionalAgreementParams);
  } catch (error) {
    const errorResponse = megaMinderStatus.KO;
    errorResponse.errorInfo = error;

    return errorResponse;
  }

  return yield call(updateCQ, input);
}

export function* confirmCQ(input) {

  const { showError, normalizeHttpErrors } = input;
  const { flowJson } = yield select(flowStore);
  const { sendFailToGenesys } = flowJson;

  const reduxUpdate = {
    successUpdate: uiNeedsActions.setConfirmationInfo,
    errorUpdate: showError,
  };

  if (sendFailToGenesys) {
    reduxUpdate.errorUpdate = null;
  }

  const apiNeeds = yield select(apisNeedsStore);

  const { cqId, backToSchoolMail } = apiNeeds;

  const confirmCQParams = {
    cqId,
    reduxUpdate,
    normalizeHttpErrors,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.confirmCQ],
    }),
  );
  try {
    const response = yield call(confirmCQBit, confirmCQParams);
    let idCO = null;
    if (response.customerOrder && response.customerOrder.code) {
      idCO = response.customerOrder.code;
      yield put(taggingActions.setCO(idCO));
      try{
        const flowInfo = yield select(flowStore);
        const { flowJson } = flowInfo;
        
        if(flowJson.savePurchaseInProgress){
          const { clientInfo } = yield select(apisNeedsStore);
          const currentUrl = new URL(window.location.href);
          const { searchParams } = currentUrl;
          const productOfferingsToUpgrade = searchParams.get("productOfferingsToUpgrade");
          let productOfferingsToUpgradeArray = [];
          if(productOfferingsToUpgrade){
            productOfferingsToUpgradeArray = productOfferingsToUpgrade.split(';')
          }
          const productOfferingsToDowngrade = searchParams.get("productOfferingsToDowngrade");
          let productOfferingsToDowngradeArray = [];
          if(productOfferingsToDowngrade){
            productOfferingsToDowngradeArray = productOfferingsToDowngrade.split(';')
          }
          const { productOffering } = yield select(offerStore);
          const purchaseInProgress = {
            ts: Date.now(),
            counter: 0,
            productOfferingsToUpgrade: productOfferingsToUpgradeArray,
            productOfferingsToDowngrade: productOfferingsToDowngradeArray,
            pack: productOffering,
          }
          const userId = clientInfo?.userIdPartyHash;
          yield call(setPurchaseInProgressInSecureUserStorage, purchaseInProgress, userId);
        }
      } catch (error){
        console.log(error);
      }
    }
  } catch (error) {
    const {
      errorCode: code,
      errorDetails: description,
    } = getErrorInfo(error);

    const flowInfo = yield select(flowStore);
    const { flowJson } = flowInfo;

    const { sendFailToGenesys } = flowJson;

    if (sendFailToGenesys) {
      return megaMinderStatus.OK;
    }

    yield call(sendToSE, { showError, normalizeHttpErrors, error: { code, description } });
    return null;
  } finally {
    if (backToSchoolMail) {
      yield call(sendBackToSchoolToSE, { showError, normalizeHttpErrors });
    }
  }
  return null;
}
